import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Send, X, SkipForward, Flag, Ban, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DRAFT_KEY = "maziara_match_draft";
function wsUrl() {
  const base = (BACKEND_URL || "").replace(/^http/, "ws");
  return `${base}/api/ws/match`;
}

export default function Match() {
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const [phase, setPhase] = useState("idle"); // idle|searching|timeout|matched|ended|limit
  const [partner, setPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState(() => (typeof window !== "undefined" && localStorage.getItem(DRAFT_KEY)) || "");
  const [connectState, setConnectState] = useState("locked"); // locked|available|requested|incoming|connected
  const [limitInfo, setLimitInfo] = useState(null);
  const [partnerTyping, setPartnerTyping] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, partnerTyping]);
  // Preserve the draft across disconnects/reloads.
  useEffect(() => {
    if (text) localStorage.setItem(DRAFT_KEY, text);
    else localStorage.removeItem(DRAFT_KEY);
  }, [text]);

  const send = useCallback((obj) => {
    if (wsRef.current?.readyState === 1) wsRef.current.send(JSON.stringify(obj));
  }, []);

  const connect = useCallback(() => {
    const ws = new WebSocket(wsUrl());
    wsRef.current = ws;
    ws.onopen = () => { setPhase("searching"); ws.send(JSON.stringify({ action: "join_queue" })); };
    ws.onmessage = (e) => {
      const m = JSON.parse(e.data);
      switch (m.type) {
        case "queued": setPhase("searching"); break;
        case "waiting_timeout": setPhase("timeout"); break;
        case "matched":
          setPartner(m); setMessages([]); setConnectState("locked"); setPhase("matched"); break;
        case "message":
          setPartnerTyping(false);
          setMessages((prev) => [...prev, { id: m.message_id, from: m.from, content: m.content }]); break;
        case "typing": setPartnerTyping(true); setTimeout(() => setPartnerTyping(false), 2500); break;
        case "message_blocked": toast.error(m.reason); break;
        case "connect_available": setConnectState((s) => (s === "locked" ? "available" : s)); break;
        case "connect_requested": setConnectState("incoming"); break;
        case "connected": setConnectState("connected"); toast.success("You're connected! Find it under Chats."); break;
        case "partner_left": setPhase("ended"); toast("The other person ended the chat."); break;
        case "ended": setPhase("ended"); break;
        case "blocked": setPhase("ended"); toast("Blocked. You won't be matched again."); break;
        case "reported": setPhase("ended"); toast("Reported. The chat has ended."); break;
        case "trial_ended": setPhase("ended"); setLimitInfo({ reason: "trial_human" }); break;
        case "match_limit": setPhase("limit"); setLimitInfo(m); break;
        case "search_cancelled": setPhase("idle"); break;
        case "error": if (m.reason === "unauthenticated") toast.error("Please sign in again."); break;
        default: break;
      }
    };
    ws.onclose = () => { if (phase === "searching" || phase === "matched") setPhase((p) => (p === "matched" ? "disconnected" : "idle")); };
    ws.onerror = () => {};
  }, [phase]);

  useEffect(() => () => { wsRef.current?.close(); }, []);

  const startSearch = () => { if (!wsRef.current || wsRef.current.readyState > 1) connect(); else { setPhase("searching"); send({ action: "join_queue" }); } };
  const cancel = () => { send({ action: "cancel" }); setPhase("idle"); };
  const sendMsg = () => { const t = text.trim(); if (!t) return; send({ action: "message", content: t, client_id: `${Date.now()}` }); setText(""); };
  const skip = () => { send({ action: "skip" }); setMessages([]); setPhase("searching"); send({ action: "join_queue" }); };
  const endChat = () => { send({ action: "end" }); setPhase("ended"); };

  // ---------- render ----------
  if (phase === "idle")
    return (
      <Center>
        <h1 className="font-display text-3xl text-[#F4F1EA]">Meet Someone Real</h1>
        <p className="mt-2 max-w-sm text-sm text-[#C8C2B5]">You'll be matched anonymously with another active adult who shares your language. No profiles, no photos.</p>
        <button data-testid="start-search" onClick={startSearch} className="mt-8 rounded-full bg-[#D4AF37] px-8 py-4 text-base font-semibold text-[#0A0908] hover:bg-[#E5C158]">Find someone</button>
      </Center>
    );

  if (phase === "searching")
    return (
      <Center>
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
        <p className="mt-4 text-sm text-[#C8C2B5]">Looking for someone to talk to…</p>
        <button onClick={cancel} className="mt-6 rounded-full border border-[#3D3428] px-6 py-2.5 text-sm text-[#C8C2B5]">Cancel</button>
      </Center>
    );

  if (phase === "timeout")
    return (
      <Center>
        <p className="font-display text-xl text-[#F4F1EA]">Still searching…</p>
        <p className="mt-2 max-w-xs text-sm text-[#8C8578]">No one's available right now. What would you like to do?</p>
        <div className="mt-6 flex flex-col gap-3">
          <button onClick={() => { setPhase("searching"); send({ action: "join_queue" }); }} className="rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-[#0A0908]">Keep searching</button>
          <button data-testid="timeout-meet-ai" onClick={() => { send({ action: "cancel" }); navigate("/app/discover"); }} className="rounded-full border border-[#3D3428] px-8 py-3 text-sm text-[#F4F1EA]">Meet an AI Persona</button>
          <button onClick={cancel} className="rounded-full px-8 py-3 text-sm text-[#8C8578]">Cancel</button>
        </div>
      </Center>
    );

  if (phase === "limit") {
    const early = limitInfo?.reason === "early_match_daily";
    return (
      <Center>
        <p className="font-display text-xl text-[#F4F1EA]" data-testid="match-limit-heading">
          {early ? "You've made today's 10 new connections" : (limitInfo?.reason === "trial_human" ? "Your free conversation has ended" : "Daily match limit reached")}
        </p>
        <p className="mt-2 max-w-xs text-sm text-[#C8C2B5]">
          {early ? "Discover more after midnight IST." : (limitInfo?.reason === "match_daily" ? "You can match again after the daily reset." : "Become a member to keep meeting new people.")}
        </p>
        {early && <p className="mt-4 max-w-xs text-xs text-[#8C8578]">Joining the waitlist won't increase today's limit.</p>}
        <button onClick={() => navigate("/app/membership")} className="mt-3 rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-[#0A0908]" data-testid="match-limit-waitlist">
          {early ? "Join the private waitlist" : "View membership"}
        </button>
        <button onClick={() => navigate("/app/home")} className="mt-3 text-sm text-[#8C8578]">Not now</button>
      </Center>
    );
  }

  if (phase === "ended")
    return (
      <Center>
        <p className="font-display text-xl text-[#F4F1EA]">The chat has ended</p>
        {limitInfo?.reason === "trial_human" && <p className="mt-2 max-w-xs text-sm text-[#C8C2B5]">Your free conversation has ended. Become a member to keep talking.</p>}
        <div className="mt-6 flex gap-3">
          <button onClick={() => { setPhase("searching"); startSearch(); }} className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#0A0908]">Find another person</button>
          <button onClick={() => navigate("/app/home")} className="rounded-full border border-[#3D3428] px-6 py-3 text-sm text-[#F4F1EA]">Home</button>
        </div>
      </Center>
    );

  if (phase === "disconnected")
    return (
      <Center>
        <p className="font-display text-xl text-[#F4F1EA]" data-testid="match-disconnected">Connection lost</p>
        <p className="mt-2 max-w-xs text-sm text-[#C8C2B5]">We lost the connection to this chat. Live matches can't be resumed, but your draft is saved for your next chat.</p>
        <div className="mt-6 flex gap-3">
          <button onClick={() => { setMessages([]); startSearch(); }} className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#0A0908]">Find someone new</button>
          <button onClick={() => navigate("/app/home")} className="rounded-full border border-[#3D3428] px-6 py-3 text-sm text-[#F4F1EA]">Home</button>
        </div>
      </Center>
    );

  // matched
  return (
    <div className="flex h-[calc(100vh-0px)] flex-col md:h-screen" data-testid="match-chat">
      <header className="flex items-center justify-between border-b border-[#2B241B] bg-[#12100E] px-4 py-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[#F4F1EA]">{partner?.alias}</span>
            <span className="rounded-full bg-[#24201C] px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#9FE0B0]">Real person</span>
          </div>
          <p className="text-xs text-[#8C8578]">{partner?.age_band} · {partner?.gender} · {partner?.language}</p>
        </div>
        <div className="flex items-center gap-1">
          <IconBtn title="Skip" onClick={skip}><SkipForward className="h-4 w-4" /></IconBtn>
          <IconBtn title="Report" onClick={() => send({ action: "report", reason: "Inappropriate behaviour" })}><Flag className="h-4 w-4" /></IconBtn>
          <IconBtn title="Block" onClick={() => send({ action: "block" })}><Ban className="h-4 w-4" /></IconBtn>
          <IconBtn title="End" onClick={endChat}><X className="h-4 w-4" /></IconBtn>
        </div>
      </header>

      {connectState !== "connected" && (
        <div className="flex items-center justify-between gap-2 border-b border-[#2B241B] bg-[#17130D] px-4 py-2 text-xs">
          {connectState === "locked" && <span className="text-[#8C8578]">Send a few messages each before you can save this connection.</span>}
          {connectState === "available" && <><span className="text-[#C8C2B5]">Enjoying this? Ask to stay connected.</span><button onClick={() => { send({ action: "connect_request" }); setConnectState("requested"); }} className="inline-flex items-center gap-1 rounded-full bg-[#24201C] px-3 py-1 text-[#D4AF37]"><UserPlus className="h-3 w-3" /> Connect</button></>}
          {connectState === "requested" && <span className="text-[#8C8578]">Connection request sent.</span>}
          {connectState === "incoming" && <><span className="text-[#C8C2B5]">They'd like to stay connected.</span><button onClick={() => send({ action: "connect_accept" })} className="rounded-full bg-[#D4AF37] px-3 py-1 font-semibold text-[#0A0908]">Accept</button></>}
        </div>
      )}

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.from === "me" ? "bg-[#D4AF37] text-[#0A0908]" : "bg-[#1E1B16] text-[#F4F1EA]"}`}>{m.content}</div>
          </div>
        ))}
        {partnerTyping && <p className="text-xs text-[#8C8578]">typing…</p>}
      </div>

      <div className="flex items-center gap-2 border-t border-[#2B241B] bg-[#12100E] px-3 py-3 safe-bottom">
        <input
          data-testid="match-input"
          value={text}
          onChange={(e) => { setText(e.target.value); send({ action: "typing" }); }}
          onKeyDown={(e) => e.key === "Enter" && sendMsg()}
          placeholder="Type a message…"
          className="flex-1 rounded-full border border-[#2B241B] bg-[#1A1714] px-4 py-2.5 text-sm text-[#F4F1EA] outline-none focus:border-[#D4AF37]/50"
        />
        <button data-testid="match-send" onClick={sendMsg} className="flex h-10 w-10 items-center justify-center rounded-full bg-[#D4AF37] text-[#0A0908]"><Send className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="flex min-h-[calc(100vh-56px)] flex-col items-center justify-center px-5 text-center md:min-h-screen">{children}</div>;
}
function IconBtn({ children, onClick, title }) {
  return <button title={title} onClick={onClick} className="flex h-8 w-8 items-center justify-center rounded-full text-[#C8C2B5] hover:bg-[#24201C] hover:text-[#D4AF37]">{children}</button>;
}
